/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import BillingInfoModel from 'cio/models/billing-info';
import SubscriptionModel from 'cio/models/subscription';
import AccountService from 'cio/services/account';
import { inject as service } from '@ember/service';
import BillingService, { CDP_STARTER_PLAN_SLUG } from 'cio/services/billing';
import AlertsService from 'cio/services/alerts';
import { task, all } from 'ember-concurrency';
import { alias } from '@ember/object/computed';
import ApiService from 'cio/services/api';
import AccountModel from 'cio/models/account';
import MasqService from 'cio/services/masq';
import DS from 'ember-data';
import { StartupType } from 'cio/utils/billing/startup';
import { action } from '@ember/object';
import { PlanType } from 'cio/models/plan';

interface PaymentMethodsArgs {
  paramPlan: string;
  subscription: SubscriptionModel;
  billingInfo: BillingInfoModel;
  account: AccountModel;
  pinnedPlan: string;
  planName: string;
  planSummary: string;
  updatedCardInformation: boolean;
  updateBillingInfoAction: () => unknown;
}

const FREE_PLANS = [CDP_STARTER_PLAN_SLUG] as const;

export default class PaymentMethods extends Component<PaymentMethodsArgs> {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service billing!: BillingService;
  @service masq!: MasqService;
  @service store!: DS.Store;

  @alias('billing.billingInfo') billingInfo!: BillingInfoModel;

  retryPayment = task(async () => {
    try {
      await this.account.current.retryPayment({});
      this.alerts.success('account.retryPayment.success', 'Payment processed');
      await all([this.account.current.reload(), this.billing.initialize({ refresh: true })]);
    } catch (res) {
      this.alerts.error(
        'account.retryPayment.failure',
        'There was a problem processing your payment. Please reach out to win@customer.io if you need additional support.',
      );
    }
  });

  get canHaveMonthlyOverages() {
    return !this.args.billingInfo?.basic && !this.args.billingInfo?.basicTrial;
  }

  refreshAccountUsageTask = task(async () => {
    await this.store.queryRecord('account-usage', {});
  });

  get isStartupAccount() {
    return (
      this.args.billingInfo.startupProgram === StartupType.Plus ||
      this.args.billingInfo.startupProgram === StartupType.Basic ||
      this.args.billingInfo.startupProgram === StartupType.Essentials
    );
  }

  get planName() {
    if (this.args.subscription.plan.planType === PlanType.Premium) {
      return 'Premium';
    }
    if (this.args.subscription.plan.planType === PlanType.Enterprise) {
      return 'Enterprise';
    }
    return this.args.subscription.plan.displayName;
  }

  get paymentSectionTitle() {
    let title = [];
    if (this.isPlanPaidPeriodically) {
      title.push(`${this.args.subscription.plan.intervalNameForDisplay} Plan`);
    }
    if (this.canHaveMonthlyOverages) {
      title.push('Monthly Overages');
    }
    return title.join(' & ');
  }

  /** Periodically paid plans are paid monthly or yearly, as opposed to
   * other arrangements like one-time payments or free plans. */
  get isPlanPaidPeriodically() {
    return !this.isFreePlan && !this.args.subscription.hasAchSetup;
  }

  get isFreePlan() {
    /** TODO: this information should be coming from the API */
    return FREE_PLANS.includes(this.args.subscription.plan.slug as (typeof FREE_PLANS)[number]);
  }

  get shouldRequestCreditCardForOverages() {
    return this.isFreePlan && this.canHaveMonthlyOverages && !this.args.subscription.hasCard;
  }

  @action
  updateBillingInfoAction() {
    return this.args.updateBillingInfoAction();
  }
}
